import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRing, Send } from "lucide-react";

export default function Settings() {
  const [form, setForm] = useState({ reminders_enabled: false, reminder_email: "", reminder_days: 3 });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({ ...r.data, reminder_email: r.data.reminder_email || "" }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", { ...form, reminder_email: form.reminder_email || null });
      toast.success("Hatırlatma ayarları kaydedildi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-reminder");
      data.sent ? toast.success(data.message) : toast.info(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Ayarlar</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Vade Hatırlatmaları</h1>
        <p className="text-sm text-slate-400 mt-1">
          Her sabah 09:00'da (İstanbul saati), vadesi yaklaşan veya geciken tahsilat ve ödemeleriniz tek bir özet e-postayla gönderilir.
        </p>
      </div>

      <form onSubmit={save} className="bg-[#131924] border border-white/5 rounded-xl p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
              <BellRing className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <div className="font-medium">E-posta hatırlatmaları</div>
              <div className="text-xs text-slate-500">Kapalıyken hiçbir e-posta gönderilmez</div>
            </div>
          </div>
          <Switch
            checked={form.reminders_enabled}
            onCheckedChange={(v) => setForm({ ...form, reminders_enabled: v })}
            data-testid="reminders-enabled-switch"
          />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Hatırlatma e-postası</Label>
          <Input
            type="email"
            value={form.reminder_email}
            onChange={(e) => setForm({ ...form, reminder_email: e.target.value })}
            placeholder="muhasebe@sirketiniz.com"
            data-testid="reminder-email-input"
            className="mt-2 bg-[#0F1522] border-white/10"
          />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Vadeden kaç gün önce</Label>
          <Select value={String(form.reminder_days)} onValueChange={(v) => setForm({ ...form, reminder_days: Number(v) })}>
            <SelectTrigger data-testid="reminder-days-select" className="mt-2 bg-[#0F1522] border-white/10 w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
              <SelectItem value="1" data-testid="reminder-days-1">1 gün önce</SelectItem>
              <SelectItem value="3" data-testid="reminder-days-3">3 gün önce</SelectItem>
              <SelectItem value="7" data-testid="reminder-days-7">7 gün önce</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-2">Bu süre içindeki ve vadesi geçmiş bekleyen kalemler özete eklenir.</p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={saving} data-testid="settings-save-button" className="bg-indigo-500 hover:bg-indigo-600">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            data-testid="send-test-reminder-button"
            className="bg-transparent border-white/10 hover:bg-white/5"
          >
            <Send className="w-4 h-4 mr-1.5" /> {testing ? "Gönderiliyor..." : "Şimdi test e-postası gönder"}
          </Button>
        </div>
      </form>
    </div>
  );
}
